/**
 * "Your customers write in" (P-7.1) for the account this page is looking at: `ar`, `en` or
 * `both`. Read once per account and shared by every screen that asks, from the same config
 * read Personality uses.
 *
 * `both` until it is known, and `both` when it cannot be read: that is every account that
 * never chose, and the setting that asks for LESS on screen is not the safe guess here, since
 * a one-language screen hides the other box. Personality calls `rememberCustomerLanguages`
 * after a save, so the next screen opened reads the new choice without a reload.
 */
import { ref, unref, watch } from 'vue';

import { wasloApi } from '../api/client.js';
import { languagesOf } from '../helpers/missing-language.js';

const cache = new Map();

/** For specs: start a case from a cold cache. */
export const resetCustomerLanguages = () => cache.clear();

/** After a save on Personality: every later read of this account gets the saved choice. */
export const rememberCustomerLanguages = (accountId, languages) => {
  cache.set(String(accountId), Promise.resolve(languagesOf(languages)));
};

const load = accountId => {
  const key = String(accountId);
  if (!cache.has(key)) {
    const promise = Promise.resolve()
      .then(() => wasloApi.getConfig(accountId))
      .then(config => languagesOf(config?.customerLanguages))
      .catch(() => {
        cache.delete(key);
        return 'both';
      });
    cache.set(key, promise);
  }
  return cache.get(key);
};

/** @param {import('vue').Ref<number|null>} accountId */
export const useCustomerLanguages = accountId => {
  const languages = ref('both');
  const read = async () => {
    const id = unref(accountId);
    if (id === null || id === undefined) {
      languages.value = 'both';
      return;
    }
    languages.value = await load(id);
  };
  read();
  watch(() => unref(accountId), read);
  return languages;
};

export default useCustomerLanguages;
